import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { setterReducer, toggleReducer } from 'frontend/state/utils';

import types from 'frontend/state/actionTypes';

import { DIFFICULTIES } from 'shared/droptimizer';

import GameData from 'shared/gameData';
import {
  getWeaponHands,
  getUpgradeInfoForBonus,
} from 'shared/items';

const selectedInstanceReducer = (state = null, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_INSTANCE:
      return payload;
    case types.LOAD_DROPTIMIZER_REPORT:
      return payload.droptimizer.instance;
    case types.RESET_DROPTIMIZER:
      return null;
    default:
      return state;
  }
};

const instanceOptionsReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_INSTANCE: {
      const instanceId = payload;
      if (!_.find(state, { instanceId })) {
        // no option set exists for this instance, create empty option set
        return [
          ...state,
          {
            instanceId,
          },
        ];
      }
      // no change needed
      return state;
    }
    case types.CHOOSE_DROPTIMIZER_ENCOUNTER: {
      const { instanceId, encounterId } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            encounterId,
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          encounterId,
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_DIFFICULTY: {
      const { instanceId, difficultyId } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            difficultyId,
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          difficultyId,
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_WARFORGE_LEVEL: {
      const { instanceId, difficulty, warforgeLevel } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            warforgeLevel: {
              ...currentOptions.warforgeLevel,
              [difficulty]: warforgeLevel,
            },
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          warforgeLevel: {
            [difficulty]: warforgeLevel,
          },
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_GEM: {
      const { instanceId, gemId } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            gemId,
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          gemId,
        },
      ];
    }
    case types.CHOOSE_DROPTIMIZER_UPGRADE_LEVEL: {
      const { instanceId, difficulty, upgradeLevel } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            upgradeLevel: {
              ...currentOptions.upgradeLevel,
              [difficulty]: upgradeLevel,
            },
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          upgradeLevel: {
            [difficulty]: upgradeLevel,
          },
        },
      ];
    }
    case types.SET_DROPTIMIZER_UPGRADE_EQUIPPED: {
      const { instanceId, difficulty, upgradeEquipped } = payload;
      const currentOptions = _.find(state, { instanceId });
      if (currentOptions) {
        return [
          ..._.reject(state, { instanceId }),
          {
            ...currentOptions,
            upgradeEquipped: {
              ...currentOptions.upgradeEquipped,
              [difficulty]: upgradeEquipped,
            },
          },
        ];
      }
      return [
        ...state,
        {
          instanceId,
          upgradeEquipped: {
            [difficulty]: upgradeEquipped,
          },
        },
      ];
    }
    case types.LOAD_DROPTIMIZER_REPORT: {
      const instanceId = payload.droptimizer.instance;
      const { droptimizer } = payload;
      const previousState = _.find(state, { instanceId }) || {};
      return [
        ..._.reject(state, { instanceId }),
        {
          instanceId,
          encounterId: droptimizer.encounter,
          difficultyId: droptimizer.difficulty,
          upgradeLevel: {
            ...previousState.upgradeLevel,
            [droptimizer.difficulty]: droptimizer.upgradeLevel,
          },
          upgradeEquipped: {
            ...previousState.upgradeEquipped,
            [droptimizer.difficulty]: droptimizer.upgradeEquipped,
          },
          warforgeLevel: {
            ...previousState.warforgeLevel,
            [droptimizer.difficulty]: droptimizer.warforgeLevel,
          },
        },
      ];
    }
    default:
      return state;
  }
};

const lootSpecReducer = (state = {}, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_LOOT_SPEC: {
      const { classId, specId, lootSpecId } = payload;
      return {
        ...state,
        [classId]: {
          ...state[classId],
          [specId]: lootSpecId,
        },
      };
    }
    default:
      return state;
  }
};

// Older reducers, initial version of droptimizer

// selected instance IDs
const instancesReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.TOGGLE_DROPTIMIZER_INSTANCE:
      return _.xor(state, [payload]);
    case types.CHOOSE_DROPTIMIZER_INSTANCE:
      return [payload];
    case types.RESET_DROPTIMIZER:
      return [];
    default:
      return state;
  }
};

const encountersReducer = (state = [], action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_ENCOUNTER: {
      const { encounterId } = payload;
      return [encounterId];
    }
    case types.RESET_DROPTIMIZER:
      return [];
    default:
      return state;
  }
};

const difficultyReducer = (state = 'raid-normal', action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_DIFFICULTY: {
      const { difficultyId } = payload;
      return difficultyId;
    }
    case types.RESET_DROPTIMIZER:
      return 'raid-normal';
    default:
      return state;
  }
};

const groupByReducer = setterReducer({
  set: types.CHOOSE_DROPTIMIZER_GROUP_BY,
  initial: 'slot',
});

const alternateWeaponReducer = weaponSlot => (state = null, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.FETCH_SIMC_ITEMS_SUCCESS: {
      // need to duplicate some sorting logic here to make sure we don't populate the default
      // with the same item in main hand and off hand

      const weapons = _.reverse(_.sortBy(_.filter(payload.bags, { slot: weaponSlot }), ['itemLevel', 'id']));
      if (_.size(weapons) > 0) {
        if (weaponSlot === 'off_hand') {
          const mainWeapons = _.reverse(
            _.sortBy(
              _.filter(
                payload.bags,
                item => {
                  const hands = getWeaponHands(item);
                  return item.slot === 'main_hand' && hands !== '2h';
                },
              ),
              ['itemLevel', 'id'],
            ),
          );
          if (_.size(mainWeapons) > 0) {
            // if first main hand weapon and off hand weapons are the same ID, use the second off hand weapon as
            // the default. this should _usually_ avoid the issue of trying to reuse 1 weapon in both slots
            if (weapons[0].guid === mainWeapons[0].guid) {
              return 1;
            }
          }
        }
        return 0;
      }
      return null;
    }
    case types.FETCH_SIMC_ITEMS_REQUEST:
    case types.FETCH_SIMC_ITEMS_FAILURE:
      return null;
    case types.CHOOSE_ALTERNATE_WEAPON: {
      const { slot, index } = payload;
      if (slot === weaponSlot) {
        return index;
      }
      return state;
    }
    default:
      return state;
  }
};

const craftedStatsReducer = (state = {}, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_CRAFTED_STAT: {
      const { specId, craftedStats } = payload;
      return {
        ...state,
        [specId]: craftedStats,
      };
    }
    case types.LOAD_DROPTIMIZER_REPORT:
      if (payload.droptimizer.specId) {
        return {
          ...state,
          [payload.droptimizer.specId]: payload.droptimizer.craftedStats,
        };
      }
      return state;
    default:
      return state;
  }
};

const craftedGemReducer = (state = {}, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case types.CHOOSE_DROPTIMIZER_CRAFTED_GEM: {
      const { specId, gemId } = payload;
      return {
        ...state,
        [specId]: gemId,
      };
    }
    case types.LOAD_DROPTIMIZER_REPORT:
      if (payload.droptimizer.specId) {
        return {
          ...state,
          [payload.droptimizer.specId]: payload.droptimizer.craftedGem,
        };
      }
      return state;
    default:
      return state;
  }
};

const handleLoadReport = field => (state, action) => {
  const { type, payload } = action;
  if (type === types.LOAD_DROPTIMIZER_REPORT) {
    return payload?.droptimizer[field];
  }
  return state;
};

export default combineReducers({
  selectedInstance: selectedInstanceReducer,
  instanceOptions: instanceOptionsReducer,
  lootSpec: lootSpecReducer,
  instances: instancesReducer,
  encounters: encountersReducer,
  difficulty: difficultyReducer,
  mainHandAlternate: alternateWeaponReducer('main_hand'),
  offHandAlternate: alternateWeaponReducer('off_hand'),
  groupBy: groupByReducer,
  showPreviousTiers: toggleReducer({
    toggle: types.TOGGLE_DROPTIMIZER_SHOW_PREVIOUS_TIERS,
    initial: false,
  }),
  craftedStats: craftedStatsReducer,
  craftedGem: craftedGemReducer,
  offSpecItems: toggleReducer({
    toggle: types.TOGGLE_DROPTIMIZER_OFF_SPEC_ITEMS,
    initial: false,
    custom: handleLoadReport('offSpecItems'),
  }),
  includeConversions: toggleReducer({
    toggle: types.TOGGLE_DROPTIMIZER_INCLUDE_CONVERSIONS,
    initial: true,
    custom: handleLoadReport('includeConversions'),
  }),
});

const base = state => state.simbot.droptimizer;

const selectedInstanceSelector = state => base(state).selectedInstance;
const instancesSelector = state => base(state).instances;
const instanceOptionsSelector = state => base(state).instanceOptions;
const selectedInstanceOptionsSelector = createSelector(
  selectedInstanceSelector,
  instanceOptionsSelector,
  (instanceId, options) => _.find(options, { instanceId }) || {},
);
const selectedEncounterSelector = createSelector(
  selectedInstanceOptionsSelector,
  options => options.encounterId,
);
const selectedDifficultySelector = createSelector(
  selectedInstanceOptionsSelector,
  options => options.difficultyId || null,
);
const selectedWarforgeLevelSelector = createSelector(
  selectedInstanceOptionsSelector,
  selectedDifficultySelector,
  (options, difficulty) => {
    return _.get(options, 'warforgeLevel', {})[difficulty] || 0;
  },
);
const selectedUpgradeLevelSelector = createSelector(
  selectedInstanceOptionsSelector,
  selectedDifficultySelector,
  (options, difficulty) => {
    return _.get(options, 'upgradeLevel', {})[difficulty] || 0;
  },
);
const upgradeEquippedSelector = createSelector(
  selectedInstanceOptionsSelector,
  selectedDifficultySelector,
  (options, difficulty) => {
    return _.get(options, 'upgradeEquipped', {})[difficulty] || false;
  },
);
const gemSelector = createSelector(
  selectedInstanceOptionsSelector,
  options => options.gemId || null,
);
const lootSpecSelector = state => base(state).lootSpec;
const encountersSelector = state => base(state).encounters;
const difficultySelector = state => base(state).difficulty;
const mainHandAlternateSelector = state => base(state).mainHandAlternate;
const offHandAlternateSelector = state => base(state).offHandAlternate;
const groupBySelector = state => base(state).groupBy;
const showPreviousTiersSelector = state => base(state).showPreviousTiers;
const reportNameSelector = createSelector(
  selectedInstanceSelector,
  selectedEncounterSelector,
  selectedDifficultySelector,
  selectedWarforgeLevelSelector,
  selectedUpgradeLevelSelector,
  (instanceId, encounterId, difficultyId, warforgeLevel, upgradeLevel) => {
    const difficulty = _.find(DIFFICULTIES, { id: difficultyId });
    const upgradeInfo = getUpgradeInfoForBonus(upgradeLevel);
    const parts = _.filter([
      'Droptimizer',
      GameData.InstanceNames[instanceId],
      _.get(difficulty, 'name'),
      warforgeLevel ? `+${warforgeLevel} ${warforgeLevel > 10 ? 'T' : 'W'}F` : null,
      GameData.EncounterNames[encounterId],
      upgradeInfo?.fullName,
    ]);
    return _.join(parts, ' • ');
  },
);
const craftedStatsSelector = state => base(state).craftedStats;
const craftedGemSelector = state => base(state).craftedGem;
const offSpecItemsSelector = state => base(state).offSpecItems;
const includeConversionsSelector = state => base(state).includeConversions;

export const selectors = {
  selectedInstance: selectedInstanceSelector,
  selectedInstanceOptions: selectedInstanceOptionsSelector,
  selectedEncounter: selectedEncounterSelector,
  selectedDifficulty: selectedDifficultySelector,
  selectedWarforgeLevel: selectedWarforgeLevelSelector,
  selectedUpgradeLevel: selectedUpgradeLevelSelector,
  upgradeEquipped: upgradeEquippedSelector,
  gem: gemSelector,
  lootSpec: lootSpecSelector,
  instances: instancesSelector,
  encounters: encountersSelector,
  difficulty: difficultySelector,
  mainHandAlternate: mainHandAlternateSelector,
  offHandAlternate: offHandAlternateSelector,
  groupBy: groupBySelector,
  showPreviousTiers: showPreviousTiersSelector,
  reportName: reportNameSelector,
  craftedStats: craftedStatsSelector,
  craftedGem: craftedGemSelector,
  offSpecItems: offSpecItemsSelector,
  includeConversions: includeConversionsSelector,
};
